/**
 * #357 auto top-up: the pure rules behind the Credits-page settings form. The checks mirror the backend's
 * (`credits.setAutoTopUp`), which stays the authority; these only let the form explain a mistake before saving.
 */
import type { CreditPackage } from '@common-reducers/CreditPurchaseThunk';

/** `credits.getAutoTopUp` response. */
export interface AutoTopUpView {
  configured: boolean;
  enabled: boolean;
  /** The rollout flag allows turning it on for this workspace. Absent (older backend) means no. */
  available?: boolean;
  packItemPriceId: string;
  thresholdCredits: number;
  monthlyLimitCents: number;
  termsVersion: string;
  spentThisMonthCents: number;
  pausedUntil: string | null;
  failures: number;
  pending: boolean;
}

export interface AutoTopUpEnableRequest {
  enabled: true;
  packItemPriceId: string;
  thresholdCredits: number;
  monthlyLimitCents: number;
  acceptTerms: true;
  termsVersion: string;
}

export type AutoTopUpSetRequest = AutoTopUpEnableRequest | { enabled: false };

/** A one-time `{N}-Credits-USD` pack the auto top-up can charge. */
export interface TopUpPack {
  itemPriceId: string;
  credits: number;
  priceCents: number;
}

export interface AutoTopUpForm {
  pack: TopUpPack | null;
  thresholdCredits: string;
  limitDollars: string;
  acceptTerms: boolean;
}

export type AutoTopUpFormError = 'pack' | 'threshold' | 'limit' | 'terms';

export type EnableRequestResult =
  | { ok: true; request: AutoTopUpEnableRequest }
  | { ok: false; error: AutoTopUpFormError };

export function packsFrom(packages: CreditPackage[]): TopUpPack[] {
  const packs: TopUpPack[] = [];
  for (const p of packages) {
    if (!p.oneTime || !p.monthly) continue;
    packs.push({ itemPriceId: p.monthly.id, credits: p.credits, priceCents: Math.round(p.monthly.price * 100) });
  }
  return packs.sort((a, b) => a.credits - b.credits);
}

export function defaultPack(packs: TopUpPack[], preferredId: string): TopUpPack | null {
  return packs.find((p) => p.itemPriceId === preferredId) ?? packs[0] ?? null;
}

export function dollarsToCents(input: string): number | null {
  const s = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const cents = Math.round(Number(s) * 100);
  return cents > 0 ? cents : null;
}

export function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, '');
}

/** Owners only. Hidden where it can't be turned on, unless it is already set up (so it can still be turned off). */
export function showAutoTopUp(view: AutoTopUpView | undefined, isOwner: boolean): boolean {
  if (!isOwner || !view) return false;
  return view.available === true || view.configured;
}

export function buildEnableRequest(form: AutoTopUpForm, termsVersion: string): EnableRequestResult {
  const { pack } = form;
  if (!pack) return { ok: false, error: 'pack' };
  const threshold = form.thresholdCredits.trim();
  const thresholdCredits = /^\d+$/.test(threshold) ? Number(threshold) : 0;
  if (thresholdCredits <= 0 || thresholdCredits > pack.credits) return { ok: false, error: 'threshold' };
  const monthlyLimitCents = dollarsToCents(form.limitDollars);
  if (monthlyLimitCents === null || monthlyLimitCents < pack.priceCents) return { ok: false, error: 'limit' };
  if (!form.acceptTerms) return { ok: false, error: 'terms' };
  return {
    ok: true,
    request: { enabled: true, packItemPriceId: pack.itemPriceId, thresholdCredits, monthlyLimitCents, acceptTerms: true, termsVersion },
  };
}

/** The backend's error message from an RTK Query error, if it sent one. */
export function serverMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const { data } = error;
  if (typeof data !== 'object' || data === null || !('message' in data)) return null;
  return typeof data.message === 'string' ? data.message : null;
}
